import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  CheckCircle2, Circle, Clock, Sparkles, Target, Zap, 
  AlertTriangle, Calendar, Plane, PartyPopper, Coffee,
  Star, Flame, Trophy, RefreshCw, MessageSquare, Save, Edit2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CELEBRATION_STICKERS = ['🎉', '⭐', '🔥', '💪', '🚀', '✨', '🏆', '💯'];

const ExecutionBoard = () => {
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('renee');
  const [activeColumn, setActiveColumn] = useState('todo');
  const [progressNotes, setProgressNotes] = useState({});
  const [editingNote, setEditingNote] = useState(null);

  const getMaxTasks = (person) => person.toLowerCase() === 'ozzy' ? 2 : 5;
  const MAX_IN_PROGRESS = 2;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [tasksRes, eventsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/tasks`, { headers }),
        axios.get(`${BACKEND_URL}/api/events`, { headers })
      ]);
      
      setTasks(tasksRes.data);
      setEvents(eventsRes.data);
      
      const savedNotes = localStorage.getItem('execution_progress_notes');
      if (savedNotes) setProgressNotes(JSON.parse(savedNotes));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getTodayStatus = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    
    for (const event of events) {
      if (today >= event.start_date && today <= event.end_date) {
        return { type: 'convention', event: event.name, icon: <PartyPopper className="h-4 w-4" />, color: 'bg-purple-500' };
      }
      if (event.travel_departure_date === today || event.travel_return_date === today) {
        return { type: 'travel', event: event.name, icon: <Plane className="h-4 w-4" />, color: 'bg-blue-500' };
      }
      if (event.load_in_date === today) {
        return { type: 'setup', event: event.name, icon: <Calendar className="h-4 w-4" />, color: 'bg-orange-500' };
      }
    }
    
    return { type: 'working', event: null, icon: <Coffee className="h-4 w-4" />, color: 'bg-green-500' };
  }, [events]);

  const sortByUrgency = (taskList) => {
    return [...taskList].sort((a, b) => {
      const priorityWeight = { high: 3, medium: 2, low: 1 };
      const daysA = Math.ceil((new Date(a.deadline) - new Date()) / (1000 * 60 * 60 * 24));
      const daysB = Math.ceil((new Date(b.deadline) - new Date()) / (1000 * 60 * 60 * 24));
      const scoreA = (priorityWeight[a.priority] || 2) * 10 - daysA;
      const scoreB = (priorityWeight[b.priority] || 2) * 10 - daysB;
      return scoreB - scoreA;
    });
  };

  const getTasksForPerson = (person) => {
    const personTasks = tasks.filter(t => 
      t.assigned_to?.toLowerCase() === person.toLowerCase() && 
      t.status !== 'completed'
    );
    return sortByUrgency(personTasks);
  };

  const getExecutionTasks = (person) => {
    const maxTasks = getMaxTasks(person);
    return getTasksForPerson(person).slice(0, maxTasks);
  };

  const getCompletedToday = (person) => {
    const today = new Date().toISOString().split('T')[0];
    return tasks.filter(t => 
      t.assigned_to?.toLowerCase() === person.toLowerCase() && 
      t.status === 'completed' &&
      t.completed_at?.startsWith(today)
    );
  };

  const getInProgressTasks = (person) => {
    return tasks.filter(t => 
      t.assigned_to?.toLowerCase() === person.toLowerCase() && 
      t.status === 'in_progress'
    );
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.put(`${BACKEND_URL}/api/tasks/${taskId}?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (newStatus === 'completed') {
        const randomSticker = CELEBRATION_STICKERS[Math.floor(Math.random() * CELEBRATION_STICKERS.length)];
        toast.success(`Task completed! ${randomSticker}`);
      } else {
        toast.success('Task updated');
      }
      
      fetchData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const saveProgressNote = (taskId, note) => {
    const newNotes = { ...progressNotes, [taskId]: note };
    setProgressNotes(newNotes);
    localStorage.setItem('execution_progress_notes', JSON.stringify(newNotes));
    setEditingNote(null);
    toast.success('Progress note saved! 📝');
  };

  const todayStatus = getTodayStatus();
  const isWorkingDay = todayStatus.type === 'working';

  const priorityColors = {
    high: 'bg-red-500/20 text-red-400 border-red-500/30',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    low: 'bg-green-500/20 text-green-400 border-green-500/30'
  };

  const renderTaskCard = (task, columnType) => {
    const isCompleted = task.status === 'completed';
    const daysUntilDeadline = Math.ceil((new Date(task.deadline) - new Date()) / (1000 * 60 * 60 * 24));
    const isOverdue = daysUntilDeadline < 0;
    const isDueSoon = daysUntilDeadline <= 2 && daysUntilDeadline >= 0;
    
    return (
      <Card 
        key={task.id} 
        className={`relative overflow-hidden transition-all duration-300
          ${isCompleted ? 'bg-green-500/10 border-green-500/30' : ''}
          ${task.status === 'in_progress' ? 'bg-blue-500/10 border-blue-500/30 ring-2 ring-blue-500/50' : ''}
          ${isOverdue && !isCompleted ? 'bg-red-500/10 border-red-500/30' : ''}
          ${isDueSoon && !isCompleted && task.status !== 'in_progress' ? 'bg-orange-500/10 border-orange-500/30' : ''}
        `}
      >
        <div className={`absolute top-0 left-0 w-1 h-full ${
          isCompleted ? 'bg-green-500' : 
          task.status === 'in_progress' ? 'bg-blue-500' : 
          isOverdue ? 'bg-red-500' : 'bg-primary'
        }`} />
        
        <CardContent className="p-3 pl-4">
          {/* Header with priority */}
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            <Badge className={`text-xs px-1.5 py-0 ${priorityColors[task.priority]}`}>
              {task.priority === 'high' && <Flame className="h-3 w-3 mr-0.5" />}
              {task.priority}
            </Badge>
            {isDueSoon && !isCompleted && (
              <Badge className="bg-orange-500/20 text-orange-400 text-xs px-1.5 py-0">
                <AlertTriangle className="h-3 w-3 mr-0.5" />
                Soon
              </Badge>
            )}
            {isOverdue && !isCompleted && (
              <Badge className="bg-red-500/20 text-red-400 text-xs px-1.5 py-0 animate-pulse">
                Overdue
              </Badge>
            )}
          </div>
          
          {/* Task title */}
          <h4 className={`font-semibold text-sm leading-tight ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
            {task.title}
          </h4>
          
          {/* Description - truncated */}
          {task.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{task.description}</p>
          )}
          
          {/* Deadline */}
          <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3 flex-shrink-0" />
            <span>{new Date(task.deadline).toLocaleDateString()}</span>
            {daysUntilDeadline > 0 && !isCompleted && (
              <span className="text-primary">({daysUntilDeadline}d)</span>
            )}
          </div>
          
          {/* Progress Notes */}
          {progressNotes[task.id] && editingNote !== task.id && (
            <div className="mt-2 p-2 bg-secondary/30 rounded text-xs">
              <div className="flex items-center gap-1 text-primary mb-1">
                <MessageSquare className="h-3 w-3" />
                <span>Note:</span>
              </div>
              <p className="text-muted-foreground line-clamp-2">{progressNotes[task.id]}</p>
            </div>
          )}
          
          {editingNote === task.id && (
            <div className="mt-2 space-y-2">
              <Textarea
                placeholder="Add progress notes..."
                defaultValue={progressNotes[task.id] || ''}
                className="text-xs min-h-[50px]"
                id={`note-${task.id}`}
              />
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  className="text-xs h-7 flex-1"
                  onClick={() => saveProgressNote(task.id, document.getElementById(`note-${task.id}`).value)}
                >
                  <Save className="h-3 w-3 mr-1" />
                  Save
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="text-xs h-7"
                  onClick={() => setEditingNote(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 mt-3">
            {columnType === 'todo' && (
              <>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="h-8 text-xs flex-1 bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/30"
                  onClick={() => updateTaskStatus(task.id, 'in_progress')}
                >
                  <Zap className="h-3 w-3 mr-1" />
                  Start
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="h-8 text-xs px-2"
                  onClick={() => setEditingNote(task.id)}
                >
                  <Edit2 className="h-3 w-3" />
                </Button>
              </>
            )}
            {columnType === 'inProgress' && (
              <>
                <Button 
                  size="sm" 
                  className="h-8 text-xs flex-1 bg-green-500 hover:bg-green-600"
                  onClick={() => updateTaskStatus(task.id, 'completed')}
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Done!
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="h-8 text-xs px-2"
                  onClick={() => setEditingNote(task.id)}
                >
                  <Edit2 className="h-3 w-3" />
                </Button>
              </>
            )}
            {columnType === 'done' && (
              <div className="text-2xl mx-auto">
                {CELEBRATION_STICKERS[Math.floor(Math.random() * CELEBRATION_STICKERS.length)]}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderExecutionBoard = (person) => {
    const maxTasks = getMaxTasks(person);
    const executionTasks = getExecutionTasks(person);
    const inProgressTasks = getInProgressTasks(person);
    const completedToday = getCompletedToday(person);
    const todoTasks = executionTasks.filter(t => t.status === 'pending');
    
    const isCEO = person.toLowerCase() === 'ozzy';
    const isBiz = person.toLowerCase() === 'biz';
    const inProgressCount = inProgressTasks.length;
    const completedCount = completedToday.length;

    // Get person title
    const getPersonTitle = () => {
      if (isCEO) return { name: 'Ozzy', title: 'CEO | Owner', emoji: '👑', color: 'amber' };
      if (isBiz) return { name: 'Biz', title: 'Business Partner | Director of VTC', emoji: '👨‍💼', color: 'blue' };
      return { name: 'Renee', title: 'Operations Manager | Exec Assistant', emoji: '👩‍💼', color: 'pink' };
    };
    const personInfo = getPersonTitle();
    
    return (
      <div className="space-y-4">
        {/* Person Header with Title */}
        <div className={`text-center py-2 px-3 rounded-lg bg-${personInfo.color}-500/10 border border-${personInfo.color}-500/20`}>
          <div className="text-lg font-bold">{personInfo.emoji} {personInfo.name}</div>
          <div className="text-xs text-muted-foreground">{personInfo.title}</div>
        </div>

        {/* Daily Rules Banner - Compact for mobile */}
        <Card className={`${isCEO ? 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border-amber-500/30' : isBiz ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-500/30' : 'bg-gradient-to-r from-pink-500/20 to-rose-500/20 border-pink-500/30'}`}>
          <CardContent className="p-3">
            <div className="flex items-start gap-2">
              <div className={`p-2 rounded-full flex-shrink-0 ${isCEO ? 'bg-amber-500/30' : isBiz ? 'bg-blue-500/30' : 'bg-pink-500/30'}`}>
                {isCEO ? <Star className="h-4 w-4 text-amber-400" /> : <Target className="h-4 w-4 text-primary" />}
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-sm">
                  {isCEO ? '👑 CEO Execution Rules' : '🎯 Daily Execution Rules'}
                </h3>
                <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  <p>✓ <strong>{maxTasks} tasks/day</strong> • Max 2 in progress</p>
                  <p>✓ Outcome-based: Check off & add next step if waiting</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Day Status Banner */}
        {!isWorkingDay && (
          <Card className={`${todayStatus.color} border-none`}>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 text-white">
                {todayStatus.icon}
                <div className="min-w-0">
                  <span className="font-bold text-sm">
                    {todayStatus.type === 'convention' && '🎪 Convention Day!'}
                    {todayStatus.type === 'travel' && '✈️ Travel Day!'}
                    {todayStatus.type === 'setup' && '🔧 Setup Day!'}
                  </span>
                  <span className="text-xs opacity-90 ml-2">Tasks optional</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Stats - Always visible */}
        <div className="grid grid-cols-3 gap-2">
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
            <CardContent className="p-2 text-center">
              <div className="text-2xl font-bold text-blue-400">{todoTasks.length}</div>
              <div className="text-xs text-muted-foreground">To Do</div>
            </CardContent>
          </Card>
          <Card className={`bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20 ${inProgressCount >= MAX_IN_PROGRESS ? 'ring-2 ring-orange-500' : ''}`}>
            <CardContent className="p-2 text-center">
              <div className="text-2xl font-bold text-orange-400">{inProgressCount}/{MAX_IN_PROGRESS}</div>
              <div className="text-xs text-muted-foreground">In Progress</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
            <CardContent className="p-2 text-center">
              <div className="text-2xl font-bold text-green-400">{completedCount}</div>
              <div className="text-xs text-muted-foreground">Done 🎉</div>
            </CardContent>
          </Card>
        </div>

        {/* Mobile Column Switcher */}
        <div className="block md:hidden">
          <div className="flex gap-1 p-1 bg-secondary/30 rounded-lg">
            <button
              onClick={() => setActiveColumn('todo')}
              className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-all ${
                activeColumn === 'todo' ? 'bg-blue-500 text-white' : 'text-muted-foreground'
              }`}
            >
              <Circle className="h-3 w-3 inline mr-1" />
              To Do ({todoTasks.length})
            </button>
            <button
              onClick={() => setActiveColumn('inProgress')}
              className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-all ${
                activeColumn === 'inProgress' ? 'bg-orange-500 text-white' : 'text-muted-foreground'
              }`}
            >
              <Clock className="h-3 w-3 inline mr-1" />
              Active ({inProgressCount})
            </button>
            <button
              onClick={() => setActiveColumn('done')}
              className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-all ${
                activeColumn === 'done' ? 'bg-green-500 text-white' : 'text-muted-foreground'
              }`}
            >
              <Trophy className="h-3 w-3 inline mr-1" />
              Done ({completedCount})
            </button>
          </div>
        </div>

        {/* Mobile: Single Column View */}
        <div className="block md:hidden">
          {activeColumn === 'todo' && (
            <div className="space-y-3">
              {todoTasks.length === 0 ? (
                <Card className="border-dashed border-2 border-blue-500/20 bg-blue-500/5">
                  <CardContent className="p-6 text-center text-muted-foreground">
                    <Sparkles className="h-8 w-8 mx-auto mb-2 text-blue-400" />
                    <p className="text-sm">All caught up! 🎉</p>
                  </CardContent>
                </Card>
              ) : (
                todoTasks.map(task => renderTaskCard(task, 'todo'))
              )}
            </div>
          )}
          {activeColumn === 'inProgress' && (
            <div className="space-y-3">
              {inProgressCount >= MAX_IN_PROGRESS && (
                <div className="text-xs text-orange-400 text-center p-2 bg-orange-500/10 rounded border border-orange-500/20">
                  ⚠️ Complete a task before starting another!
                </div>
              )}
              {inProgressTasks.length === 0 ? (
                <Card className="border-dashed border-2 border-orange-500/20 bg-orange-500/5">
                  <CardContent className="p-6 text-center text-muted-foreground">
                    <Zap className="h-8 w-8 mx-auto mb-2 text-orange-400" />
                    <p className="text-sm">Start a task!</p>
                  </CardContent>
                </Card>
              ) : (
                inProgressTasks.map(task => renderTaskCard(task, 'inProgress'))
              )}
            </div>
          )}
          {activeColumn === 'done' && (
            <div className="space-y-3">
              {completedToday.length === 0 ? (
                <Card className="border-dashed border-2 border-green-500/20 bg-green-500/5">
                  <CardContent className="p-6 text-center text-muted-foreground">
                    <Target className="h-8 w-8 mx-auto mb-2 text-green-400" />
                    <p className="text-sm">Complete tasks to celebrate! 🎯</p>
                  </CardContent>
                </Card>
              ) : (
                completedToday.map(task => renderTaskCard(task, 'done'))
              )}
            </div>
          )}
        </div>

        {/* Desktop: Three Column View */}
        <div className="hidden md:grid md:grid-cols-3 gap-4">
          {/* To Do Column */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <Circle className="h-4 w-4 text-blue-400" />
              <h3 className="font-semibold text-sm text-blue-400">Today's Tasks</h3>
              <Badge className="ml-auto bg-blue-500/20 text-blue-400 text-xs">{todoTasks.length}</Badge>
            </div>
            <div className="space-y-3 min-h-[200px]">
              {todoTasks.length === 0 ? (
                <Card className="border-dashed border-2 border-blue-500/20 bg-blue-500/5">
                  <CardContent className="p-4 text-center text-muted-foreground">
                    <Sparkles className="h-6 w-6 mx-auto mb-2 text-blue-400" />
                    <p className="text-sm">All caught up! 🎉</p>
                  </CardContent>
                </Card>
              ) : (
                todoTasks.map(task => renderTaskCard(task, 'todo'))
              )}
            </div>
          </div>

          {/* In Progress Column */}
          <div className="space-y-3">
            <div className={`flex items-center gap-2 p-2 rounded-lg border ${inProgressCount >= MAX_IN_PROGRESS ? 'bg-orange-500/20 border-orange-500/40' : 'bg-orange-500/10 border-orange-500/20'}`}>
              <Clock className="h-4 w-4 text-orange-400" />
              <h3 className="font-semibold text-sm text-orange-400">In Progress</h3>
              <Badge className={`ml-auto text-xs ${inProgressCount >= MAX_IN_PROGRESS ? 'bg-orange-500 text-white' : 'bg-orange-500/20 text-orange-400'}`}>
                {inProgressCount}/{MAX_IN_PROGRESS}
              </Badge>
            </div>
            {inProgressCount >= MAX_IN_PROGRESS && (
              <div className="text-xs text-orange-400 text-center p-2 bg-orange-500/10 rounded border border-orange-500/20">
                ⚠️ Complete a task first!
              </div>
            )}
            <div className="space-y-3 min-h-[200px]">
              {inProgressTasks.length === 0 ? (
                <Card className="border-dashed border-2 border-orange-500/20 bg-orange-500/5">
                  <CardContent className="p-4 text-center text-muted-foreground">
                    <Zap className="h-6 w-6 mx-auto mb-2 text-orange-400" />
                    <p className="text-sm">Start a task!</p>
                  </CardContent>
                </Card>
              ) : (
                inProgressTasks.map(task => renderTaskCard(task, 'inProgress'))
              )}
            </div>
          </div>

          {/* Done Column */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-2 bg-green-500/10 rounded-lg border border-green-500/20">
              <Trophy className="h-4 w-4 text-green-400" />
              <h3 className="font-semibold text-sm text-green-400">Done! 🎉</h3>
              <Badge className="ml-auto bg-green-500/20 text-green-400 text-xs">{completedCount}</Badge>
            </div>
            <div className="space-y-3 min-h-[200px]">
              {completedToday.length === 0 ? (
                <Card className="border-dashed border-2 border-green-500/20 bg-green-500/5">
                  <CardContent className="p-4 text-center text-muted-foreground">
                    <Target className="h-6 w-6 mx-auto mb-2 text-green-400" />
                    <p className="text-sm">Complete tasks! 🎯</p>
                  </CardContent>
                </Card>
              ) : (
                completedToday.map(task => renderTaskCard(task, 'done'))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-4xl font-bold font-heading tracking-tight flex items-center gap-2">
            <Zap className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-primary" />
            Execution Board
          </h1>
          <p className="text-muted-foreground text-xs sm:text-sm">
            Focus. Execute. Celebrate. 🚀
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" className="h-8">
          <RefreshCw className="h-3 w-3 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Person Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full grid grid-cols-3 h-auto">
          <TabsTrigger 
            value="renee" 
            className="text-xs sm:text-sm py-2 px-1 sm:px-3 data-[state=active]:bg-pink-500/20 data-[state=active]:text-pink-400"
          >
            <span className="hidden sm:inline mr-1">👩‍💼</span> Renee
            <span className="hidden lg:inline text-xs text-muted-foreground ml-1">Ops Mgr</span>
          </TabsTrigger>
          <TabsTrigger 
            value="biz" 
            className="text-xs sm:text-sm py-2 px-1 sm:px-3 data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400"
          >
            <span className="hidden sm:inline mr-1">👨‍💼</span> Biz
            <span className="hidden lg:inline text-xs text-muted-foreground ml-1">Dir VTC</span>
          </TabsTrigger>
          <TabsTrigger 
            value="ozzy" 
            className="text-xs sm:text-sm py-2 px-1 sm:px-3 data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400"
          >
            <span className="hidden sm:inline mr-1">👑</span> Ozzy
            <span className="hidden lg:inline text-xs text-muted-foreground ml-1">CEO</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="renee" className="mt-4">
          {renderExecutionBoard('Renee')}
        </TabsContent>
        <TabsContent value="biz" className="mt-4">
          {renderExecutionBoard('Biz')}
        </TabsContent>
        <TabsContent value="ozzy" className="mt-4">
          {renderExecutionBoard('Ozzy')}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExecutionBoard;
